import type CustomWindow from 'interfaces/Utils';
import { Microsites } from 'constants/constants';
import { type RefObject, useEffect } from 'react';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

declare let window: CustomWindow;

export interface Props {
  url: string;
  ownAudio?: string;
  noTrinityAds: boolean;
  paywallCheck?: string;
  trinityRef: RefObject<HTMLElement>;
  microsite: string;
}

const TrinityPlayer = ({ url, ownAudio, noTrinityAds, paywallCheck, trinityRef, microsite }: Props) => {
  const { userFlags: userVariants } = useOptimizely();
  const trinityReadAlongFeature =
    (
      userVariants?.trinity_read_along_feature as {
        variant: string;
      }
    )?.variant === 'on';

  useEffect(() => {
    if (window.__TRINITY_SCRIPT_LOADED__) {
      return;
    }
    const script = document.createElement('script');

    if (paywallCheck !== 'pending') {
      const playerType = paywallCheck === 'no-paywall' ? '' : 'subscription-locked';
      let id;

      switch (microsite) {
        case Microsites.Asia:
          id = 2900019104;
          break;
        case Microsites.Europe:
          id = 2900019105;
          break;
        case Microsites.Well:
          id = 2900019106;
          break;
        case Microsites.Crypto:
          id = 2900019107;
          break;
        default:
          id = trinityReadAlongFeature ? 2900023830 : 2900017491;
          break;
      }

      script.src = `https://trinitymedia.ai/player/trinity/${id}/?pageURL=${encodeURIComponent(
        url,
      )}&FAB=fabAfterClick&playerType=${playerType}`;
      if (ownAudio) {
        script.src += `&cf=${encodeURIComponent(ownAudio || '')}`;
      }
      script.async = true;
      script.type = 'text/javascript';

      if (trinityRef.current) {
        window.__TRINITY_SCRIPT_LOADED__ = true;
        trinityRef.current.appendChild(script);
      }
    }
  }, [url, trinityRef, paywallCheck, microsite]);

  useEffect(() => {
    const updateTrinityAdStatus = (event: MessageEvent) => {
      if (
        event.origin !== 'https://trinitymedia.ai' ||
        event.data?.type !== 'TRINITY_TTS' ||
        typeof window === 'undefined' ||
        !window.TRINITY_PLAYER ||
        !window.TRINITY_PLAYER.message ||
        !window.TRINITY_PLAYER.api
      ) {
        return;
      }
      if (event.data.value && event.data.value.action === window.TRINITY_PLAYER.message.playerReady && noTrinityAds) {
        window.TRINITY_PLAYER.api.setAdFreeSubscriber();
      }
    };
    window.addEventListener('message', updateTrinityAdStatus);
    return () => {
      window.removeEventListener('message', updateTrinityAdStatus);
    };
  }, [noTrinityAds]);

  return (
    <div
      className='trinity-tts-pb'
      dir='ltr'
    />
  );
};

export default TrinityPlayer;
